import React, { useState } from "react";
import {
    Button,
    Modal,
    TextField,
    Radio,
    RadioGroup,
    FormControl,
    FormLabel,
    FormControlLabel,
    Typography,
    IconButton,
    Card,
    Fade,
    CircularProgress,
} from "@mui/material";
import { Stepper, Step, StepLabel, StepConnector, styled, Box } from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PaymentIcon from "@mui/icons-material/Payment";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import toast from 'react-hot-toast';
import LeadService from '../../../core/services/leadsService';
import SalePackageImage from '../../../../public/assets/package-selection-bg-image.jpg'
import "./createAgent.css";
// Custom StepConnector with dynamic styles
const CustomStepConnector = styled(StepConnector)(({ theme }) => ({
    '& .MuiStepConnector-line': {
        borderColor: theme.palette.grey[400], // Default line color
    },
    '&.Mui-active .MuiStepConnector-line': {
        borderColor: "blue", // Line color when active
    },
    '&.Mui-completed .MuiStepConnector-line': {
        borderColor: "blue", // Line color when completed
    },
}));
// Stripe public key
const stripePromise = loadStripe("pk_live_51PIeqlEjxq0l8cmXEnBCpiZ5b17q1eEmBCTCyvjp1go9qaz2NNdkiHgQNJCZVYUMTieTOZfGtKhKYWR9QQ9AUZ3e00jMQbjluP");

const steps = [
    { label: "Add Details", icon: <PersonIcon /> },
    { label: "Choose Plan", icon: <AssignmentIcon /> },
    { label: "Add Card", icon: <PaymentIcon /> },
    { label: "Complete", icon: <CheckCircleOutlineIcon /> },
];

const PaymentForm = ({ formData, setOpen, SalesAgents, setErrors, setActiveStep, activeStep, setFormData, loading, setLoading }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isStripeReady, setIsStripeReady] = useState(false);
    React.useEffect(() => {
        // Wait until Stripe and Elements are ready
        const interval = setInterval(() => {
            if (stripe && elements) {
                setIsStripeReady(true);
                clearInterval(interval);
            }
        }, 100); // Check every 100ms
    }, [stripe, elements]);

    const handlePayment = async () => {
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            toast.error("Card details are required.");
            return;
        }
        setLoading(true)
        // Create a Stripe token
        const { token, error } = await stripe.createToken(cardElement);
        
        if (error) {
            toast.error(`Payment error: ${error.message}`);
            return;
        } else {
            try {
                const updatedFormData = {
                    stripe_token: token?.id, // Stripe token
                    phone: formData?.phone, // User's phone number
                    email: formData?.email, // User's email
                    name: formData?.name, // Full name (first + last)
                    subscription_type: formData?.plan, // Subscription plan
                };
                // Submit data to backend
                const res = await LeadService.createAgentBySales(updatedFormData);
                if (res.data.success) {
                    toast.success(res.data.message);
                    // setOpen(false);
                    setErrors({})
                    setActiveStep(activeStep + 1);
                    setFormData({
                        name: "",
                        email: "",
                        phone: "",
                        plan: "",
                    })
                    SalesAgents();
                    if (activeStep + 1 === 3) {
                        setTimeout(() => {
                            setOpen(false);
                            setActiveStep(0);
                        }, 4000); // Close the dialog after 2 seconds
                    }

                } else {
                    toast.error(res.data.message);
                }
            } catch (err) {
                toast.error(err.message);
            } finally {
                setLoading(false);
            }
        }
    };





    const cardElementOptions = {
        hidePostalCode: true, // Disables the postal code input
        hideIcon: true, // Removes the save-with-link checkbox and icon
        link: false,
        style: {
            base: {
                fontSize: "16px",
                color: "#424770",
                letterSpacing: "0.025em",
                fontFamily: "Arial, sans-serif",
                "::placeholder": {
                    color: "#aab7c4",
                },
            },
            invalid: {
                color: "#9e2146",
            },
        },
    };

    return (
        <>
            <Box className="stripe-box">
                {!isStripeReady ? (
                    <CircularProgress />
                ) : (
                    <>
                        <CardElement options={cardElementOptions} />
                        <Button
                            variant="contained"
                            onClick={handlePayment}
                            disabled={!stripe || !elements || loading}
                            style={{ marginTop: "30px" }}
                        >
                            Pay & Submit
                            {loading && <CircularProgress size={18} style={{ color: "#3b3bbb" }} />}
                        </Button>
                    </>
                )}
            </Box>
        </>

    );
};

const CreateSaleAgent = ({ SalesAgents }) => {
    const [open, setOpen] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        plan: "",
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setErrors({})
        setActiveStep(0)
        setFormData({
            name: "",
            email: "",
            phone: "",
            plan: "",
        })
    }
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const validateStep1 = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = "name is required";
        if (!formData.email) {
            newErrors.email = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = "Enter a valid email";
        }
        if (!formData.phone) newErrors.phone = "Phone number is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep2 = () => {
        if (!formData.plan) {
            setErrors({ plan: "Please select a plan" });
            return false;
        }
        return true;
    };

    const handleNext = async () => {
        if (activeStep === 0) {
            if (validateStep1()) {
                setIsLoading(true)
                const email = formData.email;
                const res = await LeadService.checkUserEmailIntelligence(email);
                if (res.data.success) {
                    toast.error(res.data.message);
                    setErrors({ email: "Email already exists" });
                    setIsLoading(false)
                    return;
                }
                setActiveStep(activeStep + 1);
                setIsLoading(false)
            }
        } else if (activeStep === 1) {
            if (validateStep2()) setActiveStep(activeStep + 1);
        }
    };

    const handleBack = () => {
        setActiveStep(activeStep - 1);
    };

    return (
        <div>
            <Button variant="contained" onClick={handleOpen} sx={{ borderRadius: '12px' }}>
                Add New Agent
            </Button>
            <Modal open={open} closeAfterTransition
                BackdropProps={{
                    timeout: 500, // Adjust the animation duration
                }}>
                <Fade in={open}>
                    <Box className="modal-box">
                        <Typography variant="h5" textAlign="center" sx={{ marginBottom: '20px' }}>
                            Add New Agent
                        </Typography>
                        <IconButton style={{ position: "absolute", top: "10px", right: "10px" }} onClick={handleClose} disabled={loading}>
                            <CloseIcon />
                        </IconButton>
                        <Stepper
                            activeStep={activeStep}
                            alternativeLabel
                            connector={<CustomStepConnector />}
                        >
                            {steps?.map((step, index) => (
                                <Step key={index}>
                                    <StepLabel
                                        icon={
                                            <Box
                                                sx={{
                                                    color: activeStep === 3 ? "green" : activeStep > index ? "#3b3bbb" : "inherit",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    width: "32px",
                                                    height: "32px",
                                                    borderRadius: "50%",
                                                    backgroundColor: activeStep === 3 ? "#e3f2fd" : activeStep > index ? "#e3f2fd" : "inherit",
                                                }}
                                            >
                                                {step.icon}
                                            </Box>
                                        }
                                    >
                                        {step.label}
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        <Box className="step-content">
                            {activeStep === 0 && (
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Box display="flex" gap={2}>
                                        <TextField
                                            name="name"
                                            label="Name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            error={!!errors.name}
                                            helperText={errors.name}
                                            fullWidth
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', }, }}
                                        />

                                    </Box>
                                    <TextField
                                        name="email"
                                        label="Email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        error={!!errors.email}
                                        helperText={errors.email}
                                        fullWidth
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', }, }}
                                    />
                                    <TextField
                                        name="phone"
                                        label="Phone Number"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        error={!!errors.phone}
                                        helperText={errors.phone}
                                        fullWidth
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', }, }}
                                    />
                                </Box>
                            )}
                            {activeStep === 1 && (
                                <FormControl sx={{ maxHeight: '480px', overflow: 'auto' }}>
                                    <FormLabel>Select a Plan</FormLabel>
                                    <RadioGroup name="plan" value={formData.plan} onChange={handleChange}>
                                        <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                                            <Radio value="founder6month" />
                                            <div style={{ marginLeft: "10px", position: "relative", }}>
                                                <img src={SalePackageImage} alt="" style={{ width: "460px", height: "110px" }} />
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        top: "16%",
                                                        left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        color: "white",
                                                        background: "#ff0000",
                                                        padding: "4px",
                                                        fontSize: "14px",
                                                        borderRadius: "8px"
                                                    }}
                                                >
                                                    Founder
                                                </span>
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        top: "36%",
                                                        left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        color: "white",
                                                        padding: "4px",
                                                        fontSize: "22px",
                                                        width: "100%",
                                                        textAlign: "center"
                                                    }}
                                                >Get 6 months package</span>
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        top: "55%",
                                                        left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        color: "white",
                                                        padding: "4px",
                                                        fontSize: "14px",
                                                        width: "100%",
                                                        textAlign: "center"
                                                    }}
                                                >
                                                    Bonus personal NFC card included
                                                </span>
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        top: "75%",
                                                        left: "45%",
                                                        transform: "translate(-50%, -50%)",
                                                        color: "white",
                                                        padding: "4px",
                                                        fontSize: "14px",
                                                        width: "100%",
                                                        textAlign: "center",
                                                        fontWeight: "bold"
                                                    }}
                                                >
                                                    <del style={{ textDecorationThickness: "2px" }}>294$</del>
                                                </span>
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        top: "75%",
                                                        left: "55%",
                                                        transform: "translate(-50%, -50%)",
                                                        color: "#000",
                                                        padding: "4px",
                                                        fontSize: "14px",
                                                        textAlign: "center",
                                                        paddingY: "4px",
                                                        background: "#fff",
                                                        paddingX: "0px",
                                                        fontWeight: "bold"
                                                    }}
                                                >
                                                    249$
                                                </span>
                                            </div>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                                            <Radio value="elite6month" />
                                            <div style={{ marginLeft: "10px", position: "relative", }}>
                                                <img src={SalePackageImage} alt="" style={{ width: "460px", height: "110px" }} />
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        top: "16%",
                                                        left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        color: "white",
                                                        background: "#ff0000",
                                                        padding: "4px",
                                                        fontSize: "14px",
                                                        borderRadius: "8px"
                                                    }}
                                                >
                                                    Elite
                                                </span>
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        top: "36%",
                                                        left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        color: "white",
                                                        padding: "4px",
                                                        fontSize: "22px",
                                                        width: "100%",
                                                        textAlign: "center"
                                                    }}
                                                >Get 6 months package</span>
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        top: "55%",
                                                        left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        color: "white",
                                                        padding: "4px",
                                                        fontSize: "14px",
                                                        width: "100%",
                                                        textAlign: "center"
                                                    }}
                                                >
                                                    Bonus personal NFC card included
                                                </span>
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        top: "75%",
                                                        left: "45%",
                                                        transform: "translate(-50%, -50%)",
                                                        color: "white",
                                                        padding: "4px",
                                                        fontSize: "14px",
                                                        width: "100%",
                                                        textAlign: "center",
                                                        fontWeight: "bold"
                                                    }}
                                                >
                                                    <del style={{ textDecorationThickness: "2px" }}>594$</del>
                                                </span>
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        top: "75%",
                                                        left: "55%",
                                                        transform: "translate(-50%, -50%)",
                                                        color: "#000",
                                                        padding: "4px",
                                                        fontSize: "14px",
                                                        textAlign: "center",
                                                        paddingY: "4px",
                                                        background: "#fff",
                                                        paddingX: "0px",
                                                        fontWeight: "bold"
                                                    }}
                                                >
                                                    399$
                                                </span>
                                            </div>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                                            <Radio value="premier6month" />
                                            <div style={{ marginLeft: "10px", position: "relative", }}>
                                                <img src={SalePackageImage} alt="" style={{ width: "460px", height: "110px" }} />
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        top: "16%",
                                                        left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        color: "white",
                                                        background: "#ff0000",
                                                        padding: "4px",
                                                        fontSize: "14px",
                                                        borderRadius: "8px"
                                                    }}
                                                >
                                                    Premier
                                                </span>
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        top: "36%",
                                                        left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        color: "white",
                                                        padding: "4px",
                                                        fontSize: "22px",
                                                        width: "100%",
                                                        textAlign: "center"
                                                    }}
                                                >Get 6 months package</span>
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        top: "55%",
                                                        left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        color: "white",
                                                        padding: "4px",
                                                        fontSize: "14px",
                                                        width: "100%",
                                                        textAlign: "center"
                                                    }}
                                                >
                                                    Bonus personal NFC card included
                                                </span>
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        top: "75%",
                                                        left: "45%",
                                                        transform: "translate(-50%, -50%)",
                                                        color: "white",
                                                        padding: "4px",
                                                        fontSize: "14px",
                                                        width: "100%",
                                                        textAlign: "center",
                                                        fontWeight: "bold"
                                                    }}
                                                >
                                                    <del style={{ textDecorationThickness: "2px" }}>1194$</del>
                                                </span>
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        top: "75%",
                                                        left: "55%",
                                                        transform: "translate(-50%, -50%)",
                                                        color: "#000",
                                                        padding: "4px",
                                                        fontSize: "14px",
                                                        textAlign: "center",
                                                        paddingY: "4px",
                                                        background: "#fff",
                                                        paddingX: "0px",
                                                        fontWeight: "bold"
                                                    }}
                                                >
                                                    899$
                                                </span>
                                            </div>
                                        </div>
                                    </RadioGroup>
                                    {errors.plan && <Typography color="error">{errors.plan}</Typography>}
                                </FormControl>
                            )}
                            {activeStep === 2 && (
                                <Card sx={{ marginTop: '12px', padding: '18px', borderRadius: '12px', boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px" }}>
                                    <Elements stripe={stripePromise}>
                                        <PaymentForm
                                            formData={formData}
                                            setOpen={setOpen}
                                            SalesAgents={SalesAgents}
                                            setErrors={setErrors}
                                            setActiveStep={setActiveStep}
                                            activeStep={activeStep}
                                            setFormData={setFormData}
                                            loading={loading}
                                            setLoading={setLoading}
                                        />
                                    </Elements>
                                </Card>

                            )}
                            {activeStep === 3 && (
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={2}
                                    py={4}
                                >
                                    <CheckCircleOutlineIcon className="text-green-500" sx={{ fontSize: '80px', color: 'green' }} />
                                    <Typography variant="h6" color="success.main" textAlign="center">
                                        Payment Successful!
                                    </Typography>
                                    <Typography color="text.secondary" textAlign="center">
                                        Your agent has been successfully added.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <Box className="step-buttons">
                            {activeStep > 0 && (
                                <>
                                    <Button
                                        onClick={handleBack}
                                        variant="outlined"
                                        disabled={loading || activeStep === 3}
                                    >
                                        Back
                                    </Button>
                                </>

                            )}
                            {activeStep < 2 && (
                                <Button onClick={handleNext} variant="contained" disabled={isLoading}>
                                    Next
                                    {isLoading && <CircularProgress size={18} style={{ color: "#3b3bbb" }} />}
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </div>
    );
};

export default CreateSaleAgent;
